"use client";
import React, { useMemo } from "react";
import { ScheduledOrderDropdown } from "../scheduledOrderDropdown/scheduledOrderDropdown";
import { SuccessBanner } from "../cartDrawer/cartStatusBanner";
import { UnavailableBanner } from "../cartDrawer/cartStatusBanner";
import { PrimaryButton } from "../button/primaryButton";
import CloseIcon from "@/components/_icons/closeIcon";
import { OrderType } from "@/types";
import { OrderTypeDropdown } from "../orderTypeDropdown/orderTypeDropdown";
import { DeliveryEstimateBanner } from "../deliveryEstimateBanner/deliveryEstimateBanner";
import { AgeRestricted } from "../ageRestricted/ageRestricted";
import { GiftCardCartItem } from "@/app/(shop)/giftcard/components/giftCardCartItem";
import { GiftCardCartOrdersBanner } from "@/app/(shop)/giftcard/components/giftCardCartOrdersBanner";
import { isBusinessOpen } from "@/utils/isBusinessOpenUtil";
import { removeFromLocalStorage } from "@/utils/localStorageUtils";
import { ScheduledTime } from "@/dto/scheduledTime";
import { getFromSessionStorage } from "@/utils/sessionStorageUtils";
import { TrashIcon } from "../_icons/trashIcon";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { RetailCartItem } from "@/dto/retailCartItem";
import { useRouter } from "next13-progressbar";
import { useCurrency } from "@/app/providers/currencyContext";

export const RestaurantCartItemView = ({
  onClose,
  merchant,
  hash,
  cartItems,
  orderType,
  setOrderType,
  scheduledOrder,
  setScheduledOrder,
  setShowScheduledOrderForm,
  removeFromCart,
  checkoutDisabled,
  merchantColor,
  handleContinueToCheckout,
  setIsRestaurantCartOpen,
  giftCard,
  setGiftCard,
}: { orderType: OrderType } & any) => {
  const router = useRouter();
  const storedSchedule = getFromSessionStorage<Partial<ScheduledTime>>(
    SessionStorageKey.SCHEDULED_TIME,
    {}
  );
  const businessOpen = useMemo(() => isBusinessOpen(merchant), [merchant]);
  const hasItemsInCart = cartItems.length > 0;
  const handleItemClick = (item: RetailCartItem) => {
    const selectedItem = item.value;
    router.push(`/menu/${selectedItem.id}`);
    onClose();
  };
  const { format } = useCurrency();

  return (
    <>
      <div className="flex flex-row justify-between items-start px-4 pt-8">
        <div>
          <h2 className="text-2xl font-bold">
            {`Your Cart (${
              cartItems.reduce(
                (adder: any, item: any) => adder + item.value?.quantity,
                0
              ) + (giftCard ? 1 : 0)
            })`}
          </h2>
        </div>

        <button
          onClick={onClose}
          className="text-gray-600 hover:text-gray-900 mr-4"
        >
          <CloseIcon />
        </button>
      </div>
      {hasItemsInCart && (
        <div className="px-4 pb-4 flex flex-row justify-between items-center gap-4 pt-8">
          <OrderTypeDropdown
            orderType={orderType}
            setOrderType={setOrderType}
            availableOrderTypes={merchant.services}
          />
          <ScheduledOrderDropdown
            scheduledOrder={scheduledOrder}
            setScheduledOrder={setScheduledOrder}
            showScheduleScreen={() => setShowScheduledOrderForm(true)}
            leadTime={merchant?.leadTime}
            merchant={merchant}
            orderType={orderType}
          />
        </div>
      )}
      {hash && hash === "#restaurant-cart" && <SuccessBanner />}
      {hash && hash === "#restaurant-cart-unavailable" && (
        <UnavailableBanner count={cartItems.length} />
      )}

      <div className="flex-grow overflow-y-auto p-4">
        {storedSchedule.orderType === OrderType.DELIVERY && hasItemsInCart && (
          <DeliveryEstimateBanner
            merchant={merchant}
            onAddMore={setIsRestaurantCartOpen}
          />
        )}

        {!businessOpen && hasItemsInCart && giftCard && (
          <GiftCardCartOrdersBanner />
        )}

        {cartItems.map((item: any, index: any) => (
          <div
            key={index}
            className="flex flex-col justify-between items-center py-4 border-b cursor-pointer"
            onClick={() => handleItemClick(item)}
          >
            <div className="w-full flex flex-row justify-between">
              <div className="w-full flex flex-col justify-between">
                <p className="text-lg text-[#373737] max-w-[310px]">
                  {item.value?.name}{" "}
                  {item.value?.quantity > 1 && `× ` + item.value?.quantity}
                </p>
                {item.value?.isAgeRestricted && (
                  <AgeRestricted text="Government-issued ID required" />
                )}
                <div className="max-w-[80%]">
                  {item.value?.modifiers.length > 0 && (
                    <p className="text-[15px] text-[#727272]">
                      {item.value?.modifiers
                        .map((modifier: any) => modifier.name)
                        .join(", ")}
                    </p>
                  )}
                  <div className="w-full">
                    <p className="text-[15px] text-[#727272] max-w-[310px] break-words">
                      {item.value?.specialInstructions}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col justify-start items-end">
                <p className="text-[16px] font-medium mt-0.5">
                  {format(item.value?.total)}
                </p>
                <button
                  onClick={(event: React.MouseEvent) =>
                    removeFromCart(event, index)
                  }
                  className="mt-2"
                >
                  <TrashIcon width={14} height={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {giftCard && (
          <GiftCardCartItem
            giftCard={giftCard}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              removeFromLocalStorage(LocalStorageKey.GIFT_CARD);
              setGiftCard(null);
            }}
            onRequestClose={onClose}
          />
        )}
      </div>
      <div className="p-4 pb-8">
        <PrimaryButton
          style={{
            backgroundColor: checkoutDisabled ? "#C7C7C7" : merchantColor,
          }}
          className="bg-black text-white font-bold py-2 rounded-md w-full h-14 disabled:bg-[#C7C7C7]"
          onClick={handleContinueToCheckout}
          disabled={checkoutDisabled}
        >
          <p className="text-xl font-medium">Continue to checkout</p>
        </PrimaryButton>
      </div>
    </>
  );
};
